import { useState } from 'react'
import {
  Trophy, RefreshCw, UserPlus, Trash2, Save, Wand2, ScrollText, ChevronLeft, Info,
} from 'lucide-react'
import {
  getAdminLeaderboard, getAdminLeaderboardTrades, adminLeaderboardSaveUser,
  adminLeaderboardSetProfit, adminLeaderboardAddUser, adminLeaderboardDelete,
  getAdminLeaderboardLog, saveAdminConfig,
} from '../../services/api'
import {
  C, Card, Row, Btn, Chips, BigStat, Loading, ErrorLine, Empty, Pill,
  useAsync, fmtDateTime, fmtInt, divider, confirmAsync, inputStyle, labelStyle,
} from './adminUI'
import {
  LB_TPW_MIN, LB_TPW_MAX, parseTradersPerWave, ratesSummary, ratesShort,
  LB_RATE_MIN, LB_RATE_MAX, parseThresholdRange, thresholdRangeText,
} from '../../utils/fundingThreshold'

// Раздел «Лидерборд»: полное управление доской + ответ на вопрос
// «откуда у юзера $1500 прибыли за 45 дней».
//
// ГЛАВНОЕ, ЧТО ЗДЕСЬ ВИДНО (и почему цифры такие) — v6-week7 (task.txt,
// 2026-09-10): доска показывает прибыль ЗА 7 ДНЕЙ.
//
// 1) «Прибыль за 7 дней» на публичной доске — это сумма PnL за последнюю неделю
//    (getLeaderboard фильтрует сделки по wave_time >= now-7д), а колонка «дней»
//    рядом — это ВОЗРАСТ участника от created_at (до 45 дней); они больше НЕ
//    согласованы между собой — так решил владелец 2026-09-10 (в v5-alltime от
//    2026-09-07 доска считала «за всё время»). Здесь показаны ОБА числа: pnl7
//    (то, что на доске) и pnlAll (вся история — контекст, доску не двигает).
//
// 2) Как набирается сумма. Ставки берутся ЖИВЫЕ с Bybit (/v5/market/tickers).
//    Волна начисляется ПО ФАКТУ появления на бирже ставки ≤ порога (−0.6%),
//    а не по таймеру: 8 ч — период начисления фандинга Bybit (00:00/08:00/16:00
//    UTC), но подходящие ставки бывают редко, поэтому волн может не быть днями.
//    В волне участник получает 1–4 монеты. База одной сделки:
//        base = |ставка %| / 100 × сумма сделки × плечо (×10)
//    При сумме сделки 700 и ставке −0.6 % это $42 за монету, до 4 монет за
//    волну → в неделю с глубокими ставками суммы крупные, в спокойную — малые.
//
// 3) Источник каждой сделки (source):
//      seed  — стартовая история при создании участника,
//      wave  — реальная волна фандинга,
//      floor — догон до минимальной прибыли ($30), если участник просел.
//    Прибыль по каждому источнику видна и в сводке, и у каждого участника.
//
// Метка «реальный / алгоритм» — поле isReal: реальные юзеры считаются по своим
// настоящим сделкам (таблица trades, только реальный счёт), алгоритмические —
// по leaderboard_trades. Править можно только алгоритмических.

const SRC_COLOR = { seed: C.indigo, wave: C.accent, floor: C.warn, unknown: C.dim, real: C.green }

// Деньги показываем без округления до копеек «на глаз»: суммы лидерборда
// хранятся с двумя знаками, но реальные сделки — с точностью биржи.
const money = (x) => {
  const n = Number(x || 0)
  let s = Math.abs(n).toFixed(8).replace(/0+$/, '')
  const [i, f = ''] = s.split('.')
  s = `${Number(i).toLocaleString('en-US')}.${f.padEnd(2, '0')}`
  return `${n < 0 ? '-' : ''}$${s}`
}

const pct = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${x}%`)

// ── Экран одного участника: построчная история «рисования» прибыли ───────────

function UserScreen({ user, onBack, onChanged }) {
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')
  const [form, setForm] = useState({
    nickname: user.nickname || '',
    capital: user.capital ?? '',
    tradeAmount: user.trade_amount ?? '',
    leverage: user.leverage ?? '',
  })
  const [target, setTarget] = useState('')

  const t = useAsync(
    () => getAdminLeaderboardTrades({ id: user.id, real: user.isReal ? 1 : 0, limit: 300 }),
    [user.id, user.isReal],
  )
  const d = t.data
  // Порог фандинга участника — строкой и подстрочником (см. fundingThreshold.js).
  const rates = ratesSummary(user.fundingRates)

  const act = async (key, fn, okMsg) => {
    setBusy(key); setNote('')
    try {
      const r = await fn()
      setNote(okMsg(r))
      t.reload()
      onChanged?.()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  const save = () => act('save', () => adminLeaderboardSaveUser({
    id: user.id,
    nickname: form.nickname,
    capital: form.capital === '' ? undefined : Number(form.capital),
    tradeAmount: form.tradeAmount === '' ? undefined : Number(form.tradeAmount),
    leverage: form.leverage === '' ? undefined : Number(form.leverage),
  }), () => 'Сохранено')

  const setProfit = async () => {
    const v = Number(target)
    if (!Number.isFinite(v)) { setNote('Ошибка: введите сумму'); return }
    if (!(await confirmAsync(
      `Привести прибыль за 7 дней к ${money(v)}? Все сделки участника за ` +
      'последнюю неделю (это и есть число на доске) будут пропорционально ' +
      'пересчитаны, знаки сохранятся.'
    ))) return
    act('profit', () => adminLeaderboardSetProfit(user.id, v),
      r => `Прибыль: ${money(r.before)} → ${money(r.after)} (${r.trades} сделок)`)
  }

  const delUser = async () => {
    if (!(await confirmAsync(`Удалить участника «${user.nickname}» со всеми его сделками?`))) return
    act('del', () => adminLeaderboardDelete({ id: user.id }),
      r => `Удалён (сделок: ${r.deletedTrades})`)
    onBack()
  }

  const delTrade = async (row) => {
    if (!(await confirmAsync(`Удалить сделку ${row.symbol} на ${money(row.pnl)}?`))) return
    act(`t${row.id}`, () => adminLeaderboardDelete({ tradeId: row.id }), () => 'Сделка удалена')
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
            background: C.card, border: `1px solid ${C.line}`, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <ChevronLeft size={17} color="#fff" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflowWrap: 'anywhere' }}>
            {user.nickname}
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
            {user.isReal ? 'реальный юзер' : 'юзер алгоритма'} · id {user.id}
            {user.alias ? ` · на доске: ${user.alias}` : ''}
          </div>
        </div>
        <Pill color={user.isReal ? C.green : C.indigo}>{user.isReal ? 'реальный' : 'алгоритм'}</Pill>
      </div>

      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      <Card title="Итоги" icon={Trophy}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <BigStat
            label="На доске (за 7 дней)"
            value={money(d?.pnl7 ?? user.pnl7)}
            color={(d?.pnl7 ?? user.pnl7) >= 0 ? C.accent : C.danger}
            hint={`${d?.trades7 ?? user.trades7 ?? 0} сделок`}
          />
          <BigStat
            label="Всё время (контекст)"
            value={money(d?.pnlAll ?? user.pnlAll)}
            color={C.indigo}
            hint={`${d?.total ?? user.tradesAll ?? 0} сделок`}
          />
        </div>
        <Row label="Баланс сейчас (старт + прибыль)" value={user.deposit != null ? money(user.deposit) : '— (реальный счёт)'} mono />
        <Row label="Стартовый депозит" value={user.capital != null ? money(user.capital) : '— (реальный счёт)'} mono />
        <Row label="Сумма сделки × плечо" value={user.capital != null
          ? `${money(user.trade_amount)} × ${user.leverage} = ${money((user.trade_amount || 0) * (user.leverage || 1))}`
          : '—'} mono />
        {/* task.txt, 2026-09-29: порог фандинга — рядом со стартовым депозитом,
            суммой сделки и плечом. У участника алгоритма это порог волны (один
            на всех), у реального юзера — MinFundingRate его ботов: у каждого
            бота он свой, поэтому показываем все и подписываем, чей какой. */}
        <Row
          label="Порог фандинга"
          value={rates.text}
          valueSub={rates.sub}
          valueColor={C.indigo}
          mono
        />
        <Row label="Дней на доске" value={fmtInt(user.days)} />
        <Row label="ROI за 7 дней" value={pct(user.roiWeek)} valueColor={C.indigo} />
        {!user.isReal && (
          <>
            <div style={divider} />
            <Row label="Из сида (стартовая история)" value={money(user.seedPnl)} mono />
            <Row label="Из волн фандинга" value={money(user.wavePnl)} mono />
            <Row label="Из догона до минимума" value={money(user.floorPnl)} mono />
          </>
        )}
      </Card>

      {!user.isReal && (
        <Card title="Правка участника" icon={Save} accent={C.warn}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={labelStyle}>Ник</div>
              <input
                style={inputStyle}
                value={form.nickname}
                onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
              />
            </div>
            <div>
              <div style={labelStyle}>Стартовый депозит, $</div>
              <input
                style={inputStyle} inputMode="decimal"
                value={form.capital}
                onChange={e => setForm(f => ({ ...f, capital: e.target.value }))}
              />
            </div>
            <div>
              <div style={labelStyle}>Сумма сделки, $</div>
              <input
                style={inputStyle} inputMode="decimal"
                value={form.tradeAmount}
                onChange={e => setForm(f => ({ ...f, tradeAmount: e.target.value }))}
              />
            </div>
            <div>
              <div style={labelStyle}>Плечо</div>
              <input
                style={inputStyle} inputMode="numeric"
                value={form.leverage}
                onChange={e => setForm(f => ({ ...f, leverage: e.target.value }))}
              />
            </div>
            <div>
              <div style={labelStyle}>Порог фандинга</div>
              {/* Участнику алгоритма порог не правится вручную: он один на всю
                  волну (диапазон) и задаётся настройкой «Диапазон порога
                  фандинга» выше. Раньше здесь не было ничего, и было непонятно,
                  по какому порогу юзер торгует. */}
              <div style={{ ...inputStyle, color: C.muted, fontFamily: 'monospace' }}>
                {rates.text}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn small icon={Save} disabled={busy === 'save'} onClick={save}>
              {busy === 'save' ? '…' : 'Сохранить'}
            </Btn>
            <Btn
              small ghost
              color={user.is_active ? C.warn : C.accent}
              disabled={busy === 'active'}
              onClick={() => act('active',
                () => adminLeaderboardSaveUser({ id: user.id, isActive: !user.is_active }),
                () => (user.is_active ? 'Скрыт с доски' : 'Возвращён на доску'))}
            >
              {user.is_active ? 'Скрыть с доски' : 'Вернуть на доску'}
            </Btn>
            <Btn small ghost color={C.danger} icon={Trash2} disabled={busy === 'del'} onClick={delUser}>
              Удалить
            </Btn>
          </div>

          <div style={divider} />
          <div style={labelStyle}>Задать прибыль за 7 дней (пересчёт сделок недели)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }} inputMode="decimal" placeholder="например 250"
              value={target}
              onChange={e => setTarget(e.target.value)}
            />
            <Btn small icon={Wand2} color={C.warn} disabled={busy === 'profit'} onClick={setProfit}>
              {busy === 'profit' ? '…' : 'Применить'}
            </Btn>
          </div>
        </Card>
      )}

      <Card title={`Сделки (${d?.rows?.length ?? 0})`} icon={ScrollText} accent={C.indigo}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={t.reload}>Обновить</Btn>}>
        <ErrorLine onRetry={t.reload}>{t.error}</ErrorLine>
        <div style={{ fontSize: 11, color: C.dim, marginBottom: 10, lineHeight: 1.45 }}>
          base = |ставка|/100 × объём × плечо — это «чистый фандинг» сделки.
          ×N — во сколько раз PnL отличается от base (меньше 0 — сделка минусовая).
        </div>
        {t.loading && !d ? <Loading /> : !(d?.rows || []).length ? <Empty>Сделок нет</Empty> : (
          d.rows.map(r => (
            <div key={r.id} style={{ borderTop: `1px solid ${C.line}`, padding: '9px 0' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#fff' }}>
                    {r.symbol}{' '}
                    <span style={{ fontSize: 10.5, color: C.dim, fontWeight: 500 }}>
                      {fmtDateTime(r.wave_time)}
                    </span>
                  </div>
                  <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2, fontFamily: 'monospace' }}>
                    {/* У виртуальных сделок funding_rate — это ставка в %, у реальных
                        в это же поле кладётся собранный фандинг в USDT. */}
                    {user.isReal
                      ? `фандинг ${money(r.funding_rate)} · объём ${money(r.volume)}`
                      : `ставка ${Number(r.funding_rate || 0).toFixed(4)}% · объём ${money(r.volume)}`}
                    {r.base != null ? ` · base ${money(r.base)}` : ''}
                    {r.factor != null ? ` · ×${r.factor}` : ''}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Pill color={SRC_COLOR[r.source] || C.dim}>{r.sourceLabel || r.source}</Pill>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontSize: 13.5, fontWeight: 700, fontFamily: 'monospace',
                    color: r.pnl >= 0 ? C.accent : C.danger,
                  }}>
                    {money(r.pnl)}
                  </div>
                  {!user.isReal && (
                    <button
                      onClick={() => delTrade(r)}
                      disabled={busy === `t${r.id}`}
                      style={{
                        marginTop: 4, background: 'transparent', border: 'none',
                        color: C.dim, fontSize: 10.5, cursor: 'pointer', padding: 0,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      удалить
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  )
}

// ── Лог начислений ──────────────────────────────────────────────────────────

function LogCard() {
  const log = useAsync(() => getAdminLeaderboardLog({ limit: 300 }), [])
  const lines = log.data?.lines || []
  return (
    <Card
      title="Лог начислений"
      icon={ScrollText}
      accent={C.muted}
      right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={log.reload}>Обновить</Btn>}
    >
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 8, lineHeight: 1.45 }}>
        Первичный след: каждая зачисленная волной сделка пишется сюда в момент начисления.
        {log.data?.file ? ` Файл: ${log.data.file}` : ''}
      </div>
      <ErrorLine onRetry={log.reload}>{log.error}</ErrorLine>
      {log.loading && !lines.length ? <Loading /> : !lines.length ? <Empty>Лог пуст</Empty> : (
        <div style={{
          maxHeight: 320, overflowY: 'auto', background: C.inner, borderRadius: 10,
          padding: '8px 10px', fontFamily: 'monospace', fontSize: 10.5,
          color: C.muted, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
        }}>
          {lines.join('\n')}
        </div>
      )}
    </Card>
  )
}

// ── Главный экран раздела ───────────────────────────────────────────────────

export default function LeaderboardSection() {
  const [filter, setFilter] = useState('all')      // all | sim | real | onboard | off
  const [sort, setSort] = useState('pnl7')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(null)
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')
  // п.3 task.txt: сколько РАЗНЫХ юзеров одновременно торгуют каждый фандинг.
  //
  // В состоянии — ЧЕРНОВИК-СТРОКА ровно такая, как её набрал админ (null =
  // «в поле ещё не лезли, показываем значение с сервера»). Раньше здесь было
  // ЧИСЛО и onChange делал parseInt(...) || 1 — из-за этого поле невозможно было
  // править: стереть прежнюю цифру, чтобы набрать новую, уже означало «пусто →
  // 1», и в поле намертво садилась единица (жалоба владельца, 2026-09-29).
  const [tpw, setTpw] = useState(null)
  const [tpwBusy, setTpwBusy] = useState('')
  // task.txt, 2026-09-30: диапазон порога фандинга алгоритма доски. Те же
  // правила, что у поля выше: в состоянии ЧЕРНОВИК-СТРОКА как её набрали
  // (null = «не трогали, показываем сервер»), разбор — при сохранении.
  const [tUp, setTUp] = useState(null)     // верхняя граница (порог отбора)
  const [tLo, setTLo] = useState(null)     // нижняя граница (пусто = без неё)
  const [tBusy, setTBusy] = useState('')

  const lb = useAsync(() => getAdminLeaderboard({ q, sort, limit: 300 }), [q, sort])
  const ov = lb.data?.overview
  const all = lb.data?.rows || []

  const rows = all.filter(r => (
    filter === 'sim' ? !r.isReal :
    filter === 'real' ? !!r.isReal :
    filter === 'onboard' ? r.onBoard :
    filter === 'off' ? !r.onBoard : true
  ))

  const addUser = async () => {
    setBusy('add'); setNote('')
    try {
      const r = await adminLeaderboardAddUser()
      setNote(`Добавлен участник id=${r.id}`)
      lb.reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  if (open) {
    // Берём свежую строку из списка (после правок цифры обновятся).
    const fresh = all.find(r => r.id === open.id && !!r.isReal === !!open.isReal) || open
    return <UserScreen user={fresh} onBack={() => setOpen(null)} onChanged={lb.reload} />
  }

  const src = ov?.bySource || {}
  const waveHours = ov?.settings?.waveMs ? Math.round(ov.settings.waveMs / 3600000) : 8

  // Редактируемая настройка «сколько юзеров торгуют каждый фандинг» (п.3).
  // Что в поле: черновик админа, а если он ещё не правил — действующее значение
  // с сервера. Проверка — только при сохранении (parseTradersPerWave), поэтому
  // поле спокойно переживает и пустое состояние, и набор цифры по одной.
  const tpwSaved = ov?.settings?.tradersPerWave
  const tpwValue = tpw !== null ? tpw : (tpwSaved != null ? String(tpwSaved) : '')
  const tpwParsed = parseTradersPerWave(tpwValue)
  const tpwFromEnv = ov?.settings?.tradersPerWaveSource === 'env'
  // Больше, чем участников в алгоритме, значение не значит ничего: движок берёт
  // Math.min(настройка, число участников). Говорим это админу прямо.
  const simTotal = ov?.simUsers?.total || 0

  const saveTpw = async () => {
    const p = parseTradersPerWave(tpwValue)
    if (!p.ok) { setNote(`Ошибка: ${p.error}`); return }
    setTpwBusy('save'); setNote('')
    try {
      const cfg = await saveAdminConfig({ leaderboard: { tradersPerWave: p.value } })
      // Сервер возвращает СОХРАНЁННЫЙ конфиг: сравниваем, чтобы админ видел,
      // если его число доехало не таким, как он ввёл (валидация на сервере).
      const got = Number(cfg?.leaderboard?.tradersPerWave)
      setTpw(null)            // поле снова показывает значение с сервера
      lb.reload()
      if (Number.isFinite(got) && got !== p.value) {
        setNote(`Сохранено ${got}, а не ${p.value}: сервер привёл значение к допустимому`)
      } else {
        setNote(`Сохранено: ${p.value} юзеров на фандинг`)
      }
    } catch (e) { setNote(`Ошибка сохранения: ${e?.message || e}`) }
    finally { setTpwBusy('') }
  }

  // Диапазон порога фандинга алгоритма (task.txt, 2026-09-30). Что в полях:
  // черновик админа, а если он их ещё не трогал — действующие значения с сервера
  // (thresholdUpper/thresholdLower; нижняя может быть null — «без границы»).
  const tUpSaved = ov?.settings?.thresholdUpper
  const tLoSaved = ov?.settings?.thresholdLower ?? null
  const tUpValue = tUp !== null ? tUp : (tUpSaved != null ? String(tUpSaved) : '')
  const tLoValue = tLo !== null ? tLo : (tLoSaved != null ? String(tLoSaved) : '')
  // Разбор — только когда админ что-то набрал: в покое поле показывает
  // серверное значение, и краснеть на нём незачем.
  const tParsed = tUp === null && tLo === null
    ? { ok: true }
    : parseThresholdRange({ upper: tUpValue, lower: tLoValue })
  const tFromEnv = ov?.settings?.thresholdSource === 'env'

  const saveRange = async () => {
    const p = parseThresholdRange({ upper: tUpValue, lower: tLoValue })
    if (!p.ok) { setNote(`Ошибка: ${p.error}`); return }
    setTBusy('save'); setNote('')
    try {
      const cfg = await saveAdminConfig({ leaderboard: { thresholdUpper: p.upper, thresholdLower: p.lower } })
      // Сервер возвращает СОХРАНЁННЫЙ конфиг: если диапазон не доехал (например,
      // нижняя оказалась не глубже верхней), админ должен это увидеть, а не
      // решить, что настройка применилась.
      const gotU = cfg?.leaderboard?.thresholdUpper
      const gotL = cfg?.leaderboard?.thresholdLower ?? null
      const sameLower = p.lower === null ? gotL === null : Number(gotL) === p.lower
      setTUp(null); setTLo(null)      // поля снова показывают значение с сервера
      lb.reload()
      if (Number(gotU) !== p.upper || !sameLower) {
        setNote(`Сервер не принял такой диапазон — сохранён прежний (${thresholdRangeText(gotU, gotL)}). Нижняя граница обязана быть глубже верхней.`)
      } else {
        setNote(`Сохранено: ${thresholdRangeText(p.upper, p.lower)} — действует со следующей волны`)
      }
    } catch (e) { setNote(`Ошибка сохранения: ${e?.message || e}`) }
    finally { setTBusy('') }
  }

  return (
    <div>
      {/* Ответ на вопрос «откуда $360» (v6-week7): цифра на доске — прибыль
          за последние 7 дней; возраст участника — отдельная величина. */}
      <div style={{
        display: 'flex', gap: 9, alignItems: 'flex-start',
        background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.25)',
        borderRadius: 12, padding: '10px 12px', marginBottom: 12,
      }}>
        <Info size={14} color={C.indigo} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 11.5, color: C.indigo, lineHeight: 1.5 }}>
          «Прибыль за 7 дней» на доске — это <b>сумма PnL за последнюю неделю</b>{' '}
          (то же окно, по которому идёт догон до минимума и ротация слабейших),
          а «дней» рядом — возраст участника (до 45) — величины независимые.
          «Всё время» в этом разделе — накопленная прибыль для контекста.
          Ставки берутся живые с Bybit;
          волна начисляется по факту появления на бирже ставки в диапазоне{' '}
          <b>{thresholdRangeText(ov?.settings?.thresholdUpper ?? ov?.settings?.threshold, ov?.settings?.thresholdLower ?? null)}</b> (период
          начисления фандинга Bybit — <b>{waveHours} ч</b>, но подходящие ставки
          бывают редко). База сделки = |ставка|/100 × сумма сделки × плечо ×
          {ov?.settings?.leverage ?? 10}.
        </div>
      </div>

      <Card
        title="Доска"
        icon={Trophy}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={lb.reload}>Обновить</Btn>}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <BigStat label="На доске" value={fmtInt((ov?.onBoard || 0) + (ov?.realOnBoard || 0))} color="#fff"
            hint={`${fmtInt(ov?.realOnBoard)} реальных`} />
          <BigStat label="Максимум" value={money(ov?.maxPnl7)} color={C.accent} hint="за 7 дней" />
          <BigStat label="Средняя" value={money(ov?.avgPnl7)} color={C.indigo} hint="за 7 дней" />
        </div>
        <Row label="Участников алгоритма" value={`${fmtInt(ov?.simUsers?.active)} активных / ${fmtInt(ov?.simUsers?.total)}`} />
        <Row label="Минимальная прибыль (порог доски)" value={money(ov?.settings?.minProfit)} mono />
        <Row label="Цель по числу участников" value={fmtInt(ov?.settings?.targetCount)} />
        <Row
          label="Волна фандинга"
          value={`${thresholdRangeText(tUpSaved ?? ov?.settings?.threshold, tLoSaved)} · период начисления ${waveHours} ч`}
        />
        {/* task.txt, 2026-09-30: ДИАПАЗОН порога фандинга алгоритма доски.
            Верхняя граница — порог отбора (наименее глубокая ставка), нижняя —
            «не глубже» (пусто = без ограничения). Раньше порог был ОДНИМ и
            вообще не правился из админки — только показывался. */}
        <div style={{ padding: '8px 2px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.35 }}>
              Диапазон порога фандинга
              <span style={{ display: 'block', fontSize: 10.5, color: C.dim }}>
                по каким ставкам работает алгоритм доски; пустая нижняя — без ограничения
              </span>
            </span>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9.5, color: C.dim, marginBottom: 2 }}>верхняя</div>
                <input
                  type="text" inputMode="decimal"
                  value={tUpValue}
                  placeholder="-0.4"
                  onChange={e => setTUp(e.target.value)}
                  style={{
                    ...inputStyle, width: 76, padding: '6px 8px', textAlign: 'center', fontSize: 13.5,
                    borderColor: tParsed.ok ? undefined : C.warn,
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 9.5, color: C.dim, marginBottom: 2 }}>нижняя</div>
                <input
                  type="text" inputMode="decimal"
                  value={tLoValue}
                  placeholder="без границы"
                  onChange={e => setTLo(e.target.value)}
                  style={{
                    ...inputStyle, width: 92, padding: '6px 8px', textAlign: 'center', fontSize: 13.5,
                    borderColor: tParsed.ok ? undefined : C.warn,
                  }}
                />
              </div>
              <Btn small icon={Save} color={C.accent} disabled={tBusy === 'save'} onClick={saveRange}>
                {tBusy === 'save' ? '…' : 'Сохранить'}
              </Btn>
            </div>
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 4, lineHeight: 1.4 }}>
            Ставка попадает в волну, если она в этом диапазоне: от {LB_RATE_MIN} до {LB_RATE_MAX} %
            (напр. верхняя −0.4 при нижней −1.5 — берутся только ставки от −1.5 до −0.4 %).
          </div>
          {/* Поля можно очистить и набрать заново — ругаемся только в момент
              сохранения, и понятным текстом. */}
          {!tParsed.ok && (tUp !== null || tLo !== null) && (
            <div style={{ fontSize: 10.5, color: C.warn, marginTop: 3 }}>{tParsed.error}</div>
          )}
          {/* Переменные окружения сервера ПЕРЕКРЫВАЮТ эту настройку. Без этой
              строки админ правил бы поля и не понимал, почему ничего не меняется. */}
          {tFromEnv && (
            <div style={{ fontSize: 10.5, color: C.warn, marginTop: 3 }}>
              На сервере задан LB_THRESHOLD (или LB_THRESHOLD_UPPER / LB_THRESHOLD_LOWER) —
              он перекрывает этот диапазон, пока его не уберут. Показано действующее значение.
            </div>
          )}
        </div>
        {/* п.3 task.txt: сколько разных юзеров одновременно торгуют каждый фандинг */}
        <div style={{ padding: '8px 2px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.35 }}>
              Юзеров на фандинг
              <span style={{ display: 'block', fontSize: 10.5, color: C.dim }}>
                сколько разных юзеров торгуют каждую подходящую ставку
              </span>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <input
                type="number" min={LB_TPW_MIN} max={LB_TPW_MAX} step={1}
                value={tpwValue}
                placeholder={tpwSaved != null ? String(tpwSaved) : '5'}
                onChange={e => setTpw(e.target.value)}
                style={{
                  ...inputStyle, width: 64, padding: '6px 8px', textAlign: 'center', fontSize: 13.5,
                  borderColor: tpwParsed.ok ? undefined : C.warn,
                }}
              />
              <Btn small icon={Save} color={C.accent} disabled={tpwBusy === 'save'} onClick={saveTpw}>
                {tpwBusy === 'save' ? '…' : 'Сохранить'}
              </Btn>
            </div>
          </div>
          {/* Поле можно очистить и набрать заново — ругаемся только в момент
              сохранения, и понятным текстом. */}
          {!tpwParsed.ok && tpw !== null && (
            <div style={{ fontSize: 10.5, color: C.warn, marginTop: 3 }}>{tpwParsed.error}</div>
          )}
          {/* Значение больше состава доски ничего не меняет: движок берёт
              Math.min(настройка, число участников). */}
          {tpwParsed.ok && simTotal > 0 && tpwParsed.value > simTotal && (
            <div style={{ fontSize: 10.5, color: C.dim, marginTop: 3 }}>
              В алгоритме сейчас {fmtInt(simTotal)} участников — больше этого числа значение не даст эффекта.
            </div>
          )}
          {/* Переменная окружения сервера ПЕРЕКРЫВАЕТ эту настройку. Без этой
              строки админ правил бы поле и не понимал, почему ничего не меняется. */}
          {tpwFromEnv && (
            <div style={{ fontSize: 10.5, color: C.warn, marginTop: 3 }}>
              На сервере задан LB_TRADERS_PER_WAVE — он перекрывает это значение,
              пока его не уберут. Показано действующее число.
            </div>
          )}
        </div>
        <Row label="Последние новые юзеры" value={ov?.settings?.lastNewUsersAt ? fmtDateTime(ov.settings.lastNewUsersAt) : 'не было'} />

        <div style={divider} />
        <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Откуда взялась прибыль</div>
        {['wave', 'seed', 'floor', 'unknown'].filter(k => src[k]).map(k => (
          <Row
            key={k}
            label={src[k].label}
            value={money(src[k].pnl7)}
            valueColor={SRC_COLOR[k]}
            valueSub={`за 7 дней · ${fmtInt(src[k].trades7)} сделок (всего за всё время ${money(src[k].pnl)} / ${fmtInt(src[k].trades)})`}
            mono
          />
        ))}
      </Card>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          placeholder="Поиск по нику…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <Btn small icon={UserPlus} disabled={busy === 'add'} onClick={addUser}>
          {busy === 'add' ? '…' : 'Добавить'}
        </Btn>
      </div>

      <Chips
        options={[
          { key: 'all',     label: 'Все',        count: all.length },
          { key: 'sim',     label: 'Алгоритм',   count: all.filter(r => !r.isReal).length },
          { key: 'real',    label: 'Реальные',   count: all.filter(r => r.isReal).length },
          { key: 'onboard', label: 'На доске',   count: all.filter(r => r.onBoard).length },
          { key: 'off',     label: 'Не на доске', count: all.filter(r => !r.onBoard).length },
        ]}
        value={filter}
        onChange={setFilter}
      />
      <Chips
        options={[
          { key: 'pnl7',   label: 'По прибыли (7д)' },
          { key: 'pnlAll', label: 'По прибыли за всё время' },
          { key: 'days',   label: 'По дням' },
          { key: 'trades7', label: 'По сделкам (7д)' },
        ]}
        value={sort}
        onChange={setSort}
      />

      <ErrorLine onRetry={lb.reload}>{lb.error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {lb.loading && !all.length ? <Loading /> : !rows.length ? <Empty>Участников нет</Empty> : (
        <Card style={{ padding: '6px 14px' }}>
          {rows.map((r, i) => (
            <div
              key={`${r.isReal}:${r.id}`}
              onClick={() => setOpen(r)}
              style={{
                borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0',
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: '#fff',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {r.nickname}
                </div>
                <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                  {r.days} дн · {r.trades7 || 0} сделок за неделю · ROI(7д) {pct(r.roiWeek)}
                  {/* deposit — «баланс сейчас» (старт + вся прибыль), то же число,
                      что юзер видит на доске. Стартовый депозит (capital) правится
                      в карточке участника. */}
                  {r.deposit != null ? ` · баланс ${money(r.deposit)}` : ''}
                  {/* Порог фандинга юзера — рядом с его числами (task.txt, 2026-09-29). */}
                  {` · ${ratesShort(r.fundingRates)}`}
                </div>
                <div style={{ display: 'flex', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                  <Pill color={r.isReal ? C.green : C.indigo}>
                    {r.isReal ? 'реальный юзер' : 'юзер алгоритма'}
                  </Pill>
                  {!r.onBoard && <Pill color={C.dim}>не на доске</Pill>}
                  {!r.is_active && <Pill color={C.warn}>скрыт</Pill>}
                  {!r.isReal && r.floorPnl > 0 && <Pill color={C.warn}>догон {money(r.floorPnl)}</Pill>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 700, fontFamily: 'monospace',
                  color: r.pnl7 >= 0 ? C.accent : C.danger,
                }}>
                  {money(r.pnl7)}
                </div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2, fontFamily: 'monospace' }}>
                  за 7 дней · всего {money(r.pnlAll)}
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}

      <LogCard />
    </div>
  )
}
